import { parseNumber } from '@utils/numberConversions'
import pick from 'lodash/pick'
import mapValues from 'lodash/mapValues'
import mapKeys from 'lodash/mapKeys'
import camelCase from 'lodash/camelCase'
import mergeWith from 'lodash/mergeWith'
import sum from 'lodash/sum'
import round from 'lodash/round'
import clamp from 'lodash/clamp'

import { R } from '@utils/currency'

export const ASSET_ALLOCATION_LOWER_BOUND = 99.90
export const ASSET_ALLOCATION_UPPER_BOUND = 100.10

export const RETIREMENT_FUNDING_ASSETS = [
  'pension',
  'provident',
  'pension_preservation',
  'provident_preservation',
  'retirement_annuity'
]

// This is a used duplication of a method that exists in the rails API
// TODO: it is not sensitive to inflation
export const interestRateIsWithinReasonableBounds = (asset) => {
  const interestRate = parseNumber(asset.interest_rate)
  return interestRate && Math.abs(interestRate) >= 0.0001 && Math.abs(interestRate) <= 0.15
}

/**
 * Determines the interest rate from a valid asset allocation entered by the user.
 * It uses the growth rates passed down from Planner. This is a duplication of
 * logic from Planner because we want immediate feedback on what the inferred interest
 * rate is.
 * Special cases:
 * 1. The inferred interest rate is clamped to [CPI, CPI + 5] - in line with the form validating
 * the interest rate within these bounds.
 *
 * @param values: Current values of the form
 * @param assetAllocationGrowthRates: Growth rates passed down from Planner.
 * @param formAssetType: The current asset type that the user is editting the form for.
 * @param inflation: Inflation for the current user. This is to ensure that the value is clamped correctly.
 *
 * @return: Inferred interest rate for the given asset allocation.
 */
export const inferInterestRateFromAssetAllocation = (values, assetAllocationGrowthRates, formAssetType, inflation) => {
  const assetAllocationValues = collectAssetAllocationValues(values)
  const selectedGrowthRates = collectGrowthRatesBasedOnAssetType(values, assetAllocationGrowthRates, formAssetType)
  const assetAllocationGrowthRatesWithStandardKeys = mapKeys(selectedGrowthRates, (_, key) => camelCase(key))

  const weightedAssetAllocations = mergeWith(assetAllocationValues, assetAllocationGrowthRatesWithStandardKeys,
    (allocation, growthRate) => allocation * growthRate
  )
  const totalGrowthRate = sum(Object.values(weightedAssetAllocations))

  const inferredInterestRate = round(totalGrowthRate, 2)

  const lowerBound = (inflation * 100)
  const upperBound = (inflation * 100) + 5.0

  return round(clamp(inferredInterestRate, lowerBound, upperBound), 2)
}

const collectAssetAllocationValues = (values) => {
  const assetAllocations = pick(values, [
    'domesticEquity',
    'domesticProperty',
    'domesticBonds',
    'domesticCash',
    'offshoreEquity',
    'offshoreProperty',
    'offshoreBonds',
    'offshoreCash'
  ])
  return mapValues(assetAllocations, value => R(value).value)
}

const collectGrowthRatesBasedOnAssetType = (values, assetAllocationGrowthRates, formAssetType) => {
  if (formAssetType === 'offshore_investment' && values.assetCurrency != 'zar') {
    return assetAllocationGrowthRates['offshore_rates'][values.assetCurrency]
  } else {
    return assetAllocationGrowthRates['domestic_rates']
  }
}
